(ns dashboard.entity.analysis.applied-analysis
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.entity.analysis.answers :as analysis.answers]
            [dashboard.entity.analysis.applied-db :as applied.db]
            [dashboard.entity.analysis.modals :as-alias analysis.modals]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.analysis.state-evidence :as justification]
            [dashboard.notifications :as notifications]
            [dashboard.ui :as ui]
            [re-frame.core :as rf]))

(defn StateDependencyTree
  [dependent-analyses]
  (let [dependencies-to-skip @(applied.db/editor-dependencies-to-skip*)
        min-depth (->> dependent-analyses
                       (map :tree/depth)
                       (apply min))]
    [:div.mx-4.analysis-list-subsection
     [:<>
      (for [{:keys [construct-id construct states
                    dependent-state-id]
             :tree/keys [depth]} dependent-analyses]
        [:div.level-left.my-1 {:key construct-id}
         [:label.checkbox.mx-2.is-flex {:for (str "skip-" construct-id)}
          (let [skipped? (contains? dependencies-to-skip construct-id)]
            [:input {:title "Include creation of dependent analysis"
                     :id (str "skip-" construct-id)
                     :type "checkbox"
                     :checked (not skipped?)
                     :on-change #(applied.db/skip-dependency
                                  construct-id
                                  (not skipped?))}])]
         [:div {:style {:display "flex"
                        :padding-left (str (* (- depth min-depth) 24) "px")}}
          (when (> depth 0)
            [:div.mr-2.mt-1.branch-icon])
          [:span
           [:b
            (analysis.profile/format-analysis-id construct-id)
            " "
            construct]
           " to "
           [analysis.profile/state-tag
            {:class ["is-small"]}
            (let [node-state-id dependent-state-id
                  node-state-id->node-state-name (into {} states)]
              (get node-state-id->node-state-name node-state-id))]]]])]]))

(defn ConstructState
  [structured-analysis editor-state set-editor-state validation-error mode]
  (let [state-id->state (into {} (:states structured-analysis))
        [hide-dependancy-tree set-hide-dependancy-tree] (react/useState false)
        selected-state-id (-> editor-state :state :state-id)]
    [:div
     [analysis.profile/field
      [:span "Construct State"]
      (if (= :view mode)
        [analysis.profile/state-tag (get state-id->state selected-state-id)]
        ;; else
        [:<>
         [:div.columns
          (analysis.profile/state-editor-radio structured-analysis
                                               selected-state-id
                                               editor-state
                                               set-editor-state)]
         (when selected-state-id
           (when-let [dependent-analyses @(applied.db/state-dependent-analyses* selected-state-id)]
             [:div.is-narrow
              [:div.is-info.notification.p-2.is-flex.mb-2.level
               {:style {:display "inline-block"}}
               [:div.ml-2
                [:i.icon.fas.fa-circle-exclamation.is-large.py-0]
                [:b "If you set this analysis to"]
                [analysis.profile/state-tag
                 {:class ["is-small" "ml-2" "mr-2"]}
                 (get state-id->state selected-state-id)]
                [:b "the following analyses' states will be set"]]
               [:button.button
                {:on-click #(set-hide-dependancy-tree not)}
                (if hide-dependancy-tree
                  [:i.icon.fas.fa-chevron-down]
                  [:i.icon.fas.fa-chevron-up])]]
              (when-not hide-dependancy-tree
                [StateDependencyTree dependent-analyses])]))])]
     (when validation-error
       [:p.help.is-danger validation-error])]))

(defn ConfirmationModal
  [{{:keys [applied-structured-analysis state-evidence state-dependent-analyses]} :ctx}]
  (let [dependencies-to-skip @(applied.db/editor-dependencies-to-skip*)]
    [:div.card
     [:header.card-header.has-background-white.is-shadowless
      [:p.card-header-title
       [:span "Additional dependencies to be created"]]
      [:button.card-header-icon.is-align-self-flex-start
       {:on-click (fn [_] (rf/dispatch [::ui/pop-modal]))}
       [:span.icon [:i.fas.fa-xmark]]]]
     [:div.card-content {:style {:max-height "75vh" :overflow-y "scroll"}}
      [StateDependencyTree state-dependent-analyses]]
     [:footer.card-footer
      [:div.card-footer-item
       [:button.button.is-primary.is-rounded
        {:on-click (fn [_]
                     (applied.db/create
                      (applied.db/make-analyses-to-create
                       applied-structured-analysis
                       (remove
                        (fn [{:keys [construct-id]}]
                          (contains? dependencies-to-skip construct-id))
                        state-dependent-analyses))
                      state-evidence))}
        "Create"]]]]))

(defn SupersededBy
  [analysis-id applied-structured-analysis latest]
  (let [{:keys [version
                applied-structured-analysis-id
                state-id
                structured-analysis-version-uuid]
         :as superseding-applied-analysis} @(analysis.db/applied-analysis*
                                             analysis-id
                                             (:next-version-id applied-structured-analysis))
        superseding-structured-analysis @(analysis.db/structured-analysis*
                                          analysis-id
                                          structured-analysis-version-uuid)
        id->state (into {} (:states superseding-structured-analysis))]
    [:div.box.has-background-important.has-text-white.is-flex.is-align-items-center.analysis-superceded-by
     [:div.mr-5
      [:i.icon.fas.fa-info-circle.is-size-3]]
     [:div.is-flex-grow-1.mx-2
      [:div.level.m-0
       [:h6.title.is-6.my-0.has-text-white
        "Analysis v"
        (:version applied-structured-analysis)
        " is superseded by v"
        version
        [analysis.profile/state-tag {:class ["is-medium" "ml-2"]} (id->state state-id)]]
       [:div
        [:button.button.is-rounded.is-small
         {:title "View analysis"
          :on-click #(rf/dispatch [::analysis.modals/show-editor-modal
                                   analysis-id
                                   :view
                                   superseding-applied-analysis])}
         (str "View v" version)]]]
      (when-not (= applied-structured-analysis-id
                   (:applied-structured-analysis-id latest))
        [:<>
         [:hr.my-2]
         [:div.level.m-0
          [:h6.title.is-6.my-0.has-text-white
           "Most recent version is v"
           (:version latest)
           [analysis.profile/state-tag
            {:class ["is-medium" "ml-2"]}
            (id->state (:state-id latest))]]
          [:div.level-right
           [analysis.profile/view-button
            #(rf/dispatch [::analysis.modals/show-editor-modal analysis-id :view latest])
            nil
            "Most Recent Version"]]]])]]))

(defn Footer
  [analysis-id
   mode
   edit-state
   dirty
   saving?
   applied-structured-analysis
   latest
   latest-structured-analysis
   state-dependent-analyses
   editor-state
   set-editor-state]
  [:footer.card-footer
   [:div.card-footer-item
    [:button.button.is-primary.is-rounded
     {:title (cond
               (some? (:next-version-id editor-state))
               "Cannot edit outdated applied analysis"

               (and (= :view mode)
                    edit-state)
               "Already editing an applied analysis"

               (and (= :view mode)
                    (nil? edit-state))
               "Open a dialog to create a new version"

               (not dirty)
               "No changes detected.\n\nMake changes to be able to create a new version of applied analysis."

               (and (= :edit mode)
                    applied-structured-analysis)
               "Create a new version of applied analysis"

               :else
               "Create new applied analysis")
      :disabled (or saving?
                    (case mode
                       ;; Don't allow transition to edit if the analysis shown is not the latest version (i.e. doesn't have a "next" pointer)
                      :view (or (some? (:next-version-id editor-state))
                                (not= (:version-id latest)
                                      (:version-id applied-structured-analysis)))
                       ;; When editing, don't allow commit, if there are no edits (edits present == "dirty")
                      :edit (and (not dirty)
                                 (= (:construct-version applied-structured-analysis)
                                    (:construct-version latest-structured-analysis)))
                      nil))
      :on-click (fn [_]
                  (if (= :view mode)
                         ;; When viewing, we offer to edit, but only for the latest version, see :disabled
                    (rf/dispatch [::analysis.modals/show-editor-modal analysis-id :edit applied-structured-analysis])
                    (let [state (:state editor-state)
                          validation-errors (applied.db/validate state)]
                      (set-editor-state (-> editor-state
                                            (assoc :validation-run true)
                                            (assoc :validation-errors validation-errors)))
                      (if validation-errors
                        (rf/dispatch [::notifications/add {:message "Found some errors, please correct them"}])
                        (applied.db/create-applied-structured-analysis
                         state
                         (:state-evidence editor-state)
                         state-dependent-analyses
                         #'ConfirmationModal)))))
      :class (when saving? "is-loading")}
     (cond
       ;; View possible only for existing applied analysis => Submit action opens new modal in :edit mode
       (= :view mode)
       "Edit Applied Analysis"

       ;; Editing possible if in :edit mode and there is an existing applied analysis => Submit action is "update" (creates new version)
       (and (= :edit mode)
            applied-structured-analysis)
       "Update Applied Analysis"

       ;; Otherwise no applied analysis exists => Submit action is to create a new one
       :else
       "Create Applied Analysis")]]])

(defn editor
  [structured-analysis applied-structured-analysis latest mode edit-state]
  (let [analysis-id (:construct-id structured-analysis)
        {:keys [evidence]} @(applied.db/linked-evidence*
                             (:applied-structured-analysis-id applied-structured-analysis))
        [editor-state set-editor-state] (react/useState {:state edit-state
                                                         :state-evidence evidence
                                                         :validation-run false
                                                         :validation-errors nil})
        ;; When in view mode, we use state from the shown applied analysis
        ;; This allows, after saving, to show the current state, instead of
        ;; what initially was present in the editor
        editor-state (if (= :view mode)
                       {:state applied-structured-analysis
                        :state-evidence evidence}
                       editor-state)

        state-id (-> editor-state :state :state-id)

        saving? @(applied.db/editor-saving*)
        state-dependent-analyses @(applied.db/state-dependent-analyses* state-id)
        all-versions @(applied.db/all-applied-analysis-versions* (:construct-id applied-structured-analysis))
        sub-analyses @(analysis.db/sub-analyses* analysis-id)
        latest-structured-analysis @(applied.db/latest-structured-analysis* analysis-id)
        dirty (if (and (= :edit mode)
                       applied-structured-analysis)
                (not= applied-structured-analysis
                      editor-state)
                true)
        field-diff (partial analysis.profile/compare-diff
                            structured-analysis
                            latest-structured-analysis)]
    (react/useEffect
     (fn []
       (when (seq evidence)
         (set-editor-state {:state edit-state
                            :state-evidence evidence
                            :validation-run false
                            :validation-errors nil}))
       (fn []))
     (array evidence))

    [:div.card.is-flex.is-flex-direction-column.is-align-items-stretch
     {:style {:max-height "90vh"}}
     [analysis.profile/card-header
      analysis-id
      (:construct structured-analysis)
      (:version applied-structured-analysis)
      (fn [_] (applied.db/close-editor mode))
      [field-diff :construct ""]
      [:<>
       [analysis.profile/categories-tags (seq (:categories structured-analysis))]
       [:span.ml-1.mr-3
        [field-diff (fn [coll] (->> coll :categories (mapv :name) str)) "Categories"]]]
      [:<>
       [analysis.profile/ConstructStateTag structured-analysis state-id]
       [:span.ml-2
        [field-diff (fn [coll] (->> coll :states (mapv second) str)) "Available States"]]]]
     [:div.card-content.pt-0
      {:style {:overflow-y "scroll"}}
      [:div
       (when (and (= :view mode)
                  applied-structured-analysis
                  (or (:next-version-id applied-structured-analysis)
                      (not= (:version-id latest) (:version-id applied-structured-analysis))))
         [SupersededBy analysis-id applied-structured-analysis latest])

       (when (or (:comment latest-structured-analysis)
                 (:comment structured-analysis))
         [analysis.profile/field
          (when-not (= (:comment structured-analysis)
                       (:comment latest-structured-analysis))
            [:<> [:span.mr-3 "Commentary"] [field-diff :comment]])
          [:span (:comment structured-analysis)]])

       (if (= :view mode)
         [analysis.profile/StateSetBy editor-state]
         ;; edit
         [:f> ConstructState
          structured-analysis
          editor-state
          set-editor-state
          (first (-> editor-state :validation-errors :state-id))
          mode])

       [:f> justification/StateEvidences
        {:sources (-> editor-state :state-evidence)
         :references @(applied.db/last-references-by-date*)
         :on-new-source #(set-editor-state
                          (update editor-state
                                  :state-evidence
                                  (fnil conj [])
                                  applied.db/empty-state-evidence))
         :on-remove-source (fn [idx]
                             (set-editor-state
                              (update-in editor-state
                                         [:state-evidence]
                                         #(vec (analysis.db/drop-nth idx (vec %))))))
         :set-field (fn [ks value]
                      (applied.db/set-evidence-field editor-state set-editor-state ks value))
         :validation-errors (:validation-errors editor-state)
         :mode mode}]

       (when (= :view mode)
         [:f> analysis.answers/AssociatedAnswers
          applied-structured-analysis
          all-versions
          (fn [version] (rf/dispatch [::analysis.modals/show-editor-modal analysis-id :view version]))
          @(rf/subscribe [::applied.db/linked-answers analysis-id])
          identity])

       [analysis.profile/Product
        (-> editor-state :state :product)
        #(applied.db/set-field editor-state set-editor-state [:product] %)
        mode]

       (when (or (seq (:relates-to structured-analysis))
                 (seq (:relates-to latest-structured-analysis)))
         [analysis.profile/field
          [:<> [:span.mr-2 "Analysis relates to"] [field-diff :relates-to "Relates To"]]
          (str/join ", " (seq (:relates-to structured-analysis)))])

       (when (or (seq (:excludes structured-analysis))
                 (seq (:excludes latest-structured-analysis)))
         [analysis.profile/field
          [:<> [:span.mr-2 "Analysis excludes"] [field-diff :excludes]]
          (str/join ", " (seq (:excludes structured-analysis)))])

       (when-let [parent-id (:parent-id structured-analysis)]
         (let [parent-analysis @(analysis.db/structured-analysis* parent-id)]
           ;; Show the latest applied analysis for parent
           [:f> analysis.profile/parent-dependencies-view
            parent-analysis
            structured-analysis
            (when (:applied-analyses parent-analysis)
              #(rf/dispatch [::analysis.modals/show-editor-modal (:construct-id parent-analysis) :view nil]))]))

       (when (seq sub-analyses)
         [:f> analysis.profile/child-dependencies-view
          sub-analyses
          (:states structured-analysis)
          #(rf/dispatch [::analysis.modals/show-editor-modal % :view nil])])

       [:f> analysis.profile/expandable-hierarchy-table
        analysis-id
        (fn [structured-analysis]
          (when (analysis.db/is-latest-asa-outdated?
                 structured-analysis
                 @(applied.db/all-applied-analysis-versions*
                   (:construct-id structured-analysis)))
            "has-background-warning-light"))
        (constantly nil)
        #(rf/dispatch [::analysis.modals/show-editor-modal (:construct-id %) :view nil])
        #(analysis.db/hierarchy* %)]

       (when (= :view mode)
         [:f> analysis.profile/version-history
          all-versions
          (:version-id applied-structured-analysis)
          (fn [version] (rf/dispatch [::analysis.modals/show-editor-modal analysis-id :view version]))])]]

     [Footer
      analysis-id
      mode
      edit-state
      dirty
      saving?
      applied-structured-analysis
      latest
      latest-structured-analysis
      state-dependent-analyses
      editor-state
      set-editor-state]]))

(defn editor-modal [{{:keys [analysis-id mode applied-structured-analysis edit-state]} :ctx}]
  (let [latest  @(applied.db/latest-applied-structured-analysis* analysis-id)
        applied-structured-analysis (or applied-structured-analysis
                                        latest)
        ;; TODO: If we have an existing applied analysis, and there is a new structured analysis available,
        ;;       the new structure might not contain the state that was selected in the existing applied analysis
        structured-analysis (if (or (= :edit mode)
                                    (not applied-structured-analysis))
                              ;; When editing, use always the latest structured analysis, since that is the basis that new versions should be created upon
                              ;; Also use latest structured analysis, if there is no applied analysis present
                              @(applied.db/latest-structured-analysis* analysis-id)
                              @(analysis.db/structured-analysis*
                                analysis-id
                                (:structured-analysis-version-uuid applied-structured-analysis)))]
    [:f> editor structured-analysis applied-structured-analysis latest mode edit-state]))
